import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FilterConfig, FilterCriteria, FilterData, FilterTag } from '../../filter';
import { TagContainerStylePipe } from './tag-container-style.pipe';
import * as Utils from '../../utils';
import { DOCUMENT } from '@angular/common';
import { ChangeDetector } from '@wfm/core';

export class FilterTagModel {
  label: string | undefined;
  filterTag: FilterTag;
  fcIndex: number;
}

@Component({
  selector: 'virtual-grid-filter-tag-panel',
  templateUrl: './virtual-grid-filter-tag-panel.component.html',
  styleUrls: ['./virtual-grid-filter-tag-panel.component.scss'],
  providers: [TagContainerStylePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VirtualGridFilterTagPanelComponent extends ChangeDetector implements OnChanges, OnDestroy {
  private _oldPanelWidth: number;
  private _resizeEventId: number;

  isMultiLine = false;
  isCollapsed = true;
  lineHeight: number = undefined;
  showToggle = false;
  filterTagModelList: FilterTagModel[] = [];

  @Input()
  excludedColumnIds: string[];

  @Input()
  filterCriteria: FilterCriteria[];

  @Input()
  headerLabel: string;

  @Input()
  showAllTags: boolean;

  @Output()
  filterCriteriaChange = new EventEmitter<FilterCriteria[]>();

  constructor(@Inject(DOCUMENT) document: any, cdref: ChangeDetectorRef) {
    super(document.defaultView, cdref);
  }

  @HostListener('window:resize')
  onResize(): void {
    // The tagPanel will only exist when there are tags to display.
    if (this._isCollapsingMode() && this.tagPanel) {
      if (this._resizeEventId) {
        this.window.clearTimeout(this._resizeEventId);
      } else {
        this._onResizeStart();
      }
      this._resizeEventId = this.window.setTimeout(() => {
        this._onResizeEnd();
      }, 500);
      this._onResize(this.tagPanel?.nativeElement?.clientWidth);
    }
  }

  @ViewChild('tagContainer')
  tagContainer: ElementRef<HTMLDivElement>;

  @ViewChild('tagPanel')
  tagPanel: ElementRef<HTMLDivElement>;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filterCriteria || changes.excludedColumnIds) {
      this._refreshFilterTagModelList();
    }

    if (changes.showAllTags) {
      this._evaluateFlags();
    }

    if (this._isCollapsingMode()) {
      // Determine the initial (old) panel width.
      if (this.filterTagModelList.length > 0) {
        if (this._oldPanelWidth === undefined) {
          this.window.setTimeout(() => {
            this._oldPanelWidth = this.tagPanel?.nativeElement?.clientWidth;
          });
        }
      } else {
        this._oldPanelWidth = undefined;
      }
    }
  }

  ngOnDestroy() {
    //For WFM usage
  }

  onClearAll(): void {
    Utils.assert(this._isCollapsingMode(), 'Should only handle clear all in collapsing mode.');
    this.filterCriteriaChange.emit([]);
  }

  onRemoveTag(filterTagModel: FilterTagModel): void {
    const index: number = filterTagModel.fcIndex;
    const newFilterData: FilterData = filterTagModel.filterTag.remove();
    if (newFilterData?.hasFilter()) {
      this.filterCriteria[filterTagModel.fcIndex].filterData = newFilterData;
    } else {
      this.filterCriteria.splice(index, 1);
    }
    this._refreshFilterTagModelList();
    this.filterCriteriaChange.emit(this.filterCriteria);
  }

  private _evaluateFlags(): void {
    if (this._isCollapsingMode()) {
      this.window.setTimeout(() => {
        const firstElement: any = this.tagContainer?.nativeElement?.firstElementChild;
        const lastElement: any = this.tagContainer?.nativeElement?.lastElementChild;

        this.lineHeight = firstElement?.offsetHeight;

        const isMultiLine =
          !!firstElement &&
          !!lastElement &&
          firstElement.offsetTop < lastElement.offsetTop &&
          firstElement.offsetTop + firstElement.offsetHeight < lastElement.offsetTop + lastElement.offsetHeight;
        if (this.isMultiLine !== isMultiLine) {
          this.isMultiLine = isMultiLine;
          this.isCollapsed = true;
          // When resizing from a smaller to larger window, we hide the toggle button so the space can be taken
          // up by filter tags and we can more accurately determine if multiple lines are necessary.
          if (this.isMultiLine && !this._isResizing()) {
            this.showToggle = true;
          }
          this.$detectChanges();
        }
      }, 50);
    }
  }

  private _isCollapsingMode(): boolean {
    return !this.showAllTags;
  }

  private _isResizing(): boolean {
    return this._resizeEventId !== undefined;
  }

  private _onResize(newPanelWidth: number): void {
    Utils.assert(this._isCollapsingMode(), 'Should only handle resize in collapsing mode.');
    // We want to show the toggle button if the panel is being sized smaller.  Otherwise we temporarily hide
    // it until the sizing is done.
    const showToggle = this._oldPanelWidth >= newPanelWidth;
    if (this.showToggle !== showToggle) {
      this.showToggle = showToggle;
      this.$detectChanges();
    }
    this._oldPanelWidth = newPanelWidth;
    this._evaluateFlags();
  }

  private _onResizeEnd(): void {
    Utils.assert(this._isCollapsingMode(), 'Should only handle resize in collapsing mode.');
    if (this.isMultiLine) {
      this.showToggle = true;
      this.$detectChanges();
    }
  }

  private _onResizeStart(): void {
    Utils.assert(this._isCollapsingMode(), 'Should only handle resize in collapsing mode.');
    // Nothing specific to do for now.
  }

  private _refreshFilterTagModelList(): void {
    const filterTagModelList: FilterTagModel[] = [];

    this.filterCriteria?.forEach((fc, fcIndex) => {
      if (!this.excludedColumnIds?.includes(fc.column.id)) {
        const config: FilterConfig = fc.column.filterConfig;
        const label: string = config.filterTagLabel !== false ? config.filterTagLabel || fc.column.headerLabel : '';

        fc.filterData.getTags(config.filterParams).forEach(filterTag => {
          filterTagModelList.push({
            label: label,
            filterTag: filterTag,
            fcIndex: fcIndex
          });
        });
      }
    });

    this.filterTagModelList = filterTagModelList;
    this.$detectChanges();

    this._evaluateFlags();
  }
}
